import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { NgxMaskPipe } from 'ngx-mask';

import { Types } from '../../../domain/types.domain';
import {
  LoginAccount,
  LoginByIntegrationOutput,
} from '../../../domain/auth/dtos/login-by-integration.output';

import { AppHandler } from '../../app.handler';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../builder/instructions/repositories/user.repository.instructions';
import { LoginByIntegrationServiceInstruction } from '../../../builder/instructions/services/login-by-integration.service.instruction';
import { ResendAccountConfirmationServiceInstruction } from '../../../builder/instructions/services/resend-account-service.instruction';

import { User } from '../../../domain/user/entities/user.entity';
import { accountConflictError } from '../../utils/account-conflict';
import {
  ACCOUNT_CREATION_INTENT,
  EstadoExterno,
  decodeState,
} from '../../utils/state-handler';
import {
  govBrAccountCreationUrl,
  govBrAuthorizeBaseUrl,
  postAuthContinuation,
} from '../../utils/govbr-authorize';
import { RedirectService } from '../../redirect.service';
import { RegistrationCompletionState } from '../registration-completion/registration-completion.component';
import { lastAccessedAccount, lastAccessedFirst } from '../../../domain/user/last-accessed-account';
import { IconComponent } from '../../components/icon/icon.component';

@Component({
  selector: 'app-login-integration',
  standalone: true,
  imports: [RouterOutlet, NgxMaskPipe, IconComponent],
  providers: [
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
    new LoginByIntegrationServiceInstruction(),
    new ResendAccountConfirmationServiceInstruction(),
  ],
  templateUrl: './login-integration.component.html',
  styleUrls: ['./login-integration.component.scss'],
})
export class LoginIntegrationComponent {
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);

  private readonly _loginByIntegrationService = inject(
    Types.LoginByIntegrationService
  );
  private readonly _resendAccountConfirmationService = inject(
    Types.ResendAccountConfirmationService
  );
  private readonly _authRepository = inject(Types.AuthRepository);

  private cityId: number = this._route.snapshot.data['city']['index'];
  public cityIndex: number = this._route.snapshot.data['city']['index'];
  public cityLogo: string | undefined = undefined;
  public cityName: string = '';
  public cityPhone: string = '';
  public cityIdentity: string = this._route.snapshot.data['city']['identidade'];
  public cityConnection: string =
    this._route.snapshot.data['city']['connection'];
  public cityLoginUrl: string = '';

  public routeState = this._router.getCurrentNavigation()?.extras?.state as any;

  public requireSelectAccount = signal(false);
  public requireCompleteRegistration = signal(false);
  public accounts = signal<LoginAccount[]>([]);
  public canCreateAccount = signal(false);
  private customUrlRedirect = '';
  public lastAccessed = computed(() => lastAccessedAccount(this.accounts()));

  public redirectUri = signal('');

  ngOnInit(): void {
    this._appHandler.setBackButtonHome(false);

    this._route.data.subscribe({
      next: ({ city }) => {
        this.cityId = city.index;
        this.cityLogo = city.logo;
        this.cityIndex = city.index;
        this.cityIdentity = city.identidade;
        this.cityConnection = city.connection;
        this.cityName = city.title;
        this.cityPhone = city.tel;
        this.cityLoginUrl = city.loginUrl;

        const params = {
          ...this._route.snapshot.params,
          ...this._route.snapshot.queryParams,
          ...(this.routeState ?? {}),
        };

        this.callbackSubscribe(params);
      },
    });
  }

  redirectToLogin(): void {
    if (this.cityLoginUrl) {
      window.location.href = this.cityLoginUrl;
      return;
    }

    this._router.navigate([`/${this.cityIdentity}`]);
  }

  getUserAbreviation(user: User) {
    const words = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);
    if (!words.length) {
      return (user?.email?.[0] ?? '').toUpperCase();
    }
    return words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0][0];
  }

  hasName(account: User): boolean {
    return Boolean(account.name?.trim());
  }

  isCompany(account: User): boolean {
    return account.type === 'business';
  }

  selectAccountToLogin(account: LoginAccount): void {
    if (account.blocked) {
      return;
    }

    if (!account.emailVerified) {
      this._appHandler.setError(true, {
        message: 'A verificação da conta está pendente.',
        description:
          'Ação necessária para acessar o sistema. Clique no botão abaixo.',
        actionText: 'Reenviar e-mail de confirmação de conta',
        action: () => {
          this.resendAccountConfirmation(account.userId);
        },
      });

      return;
    }

    if (account.registrationToken) {
      this.openRegistrationCompletion(account.registrationToken, account);
      return;
    }

    this._redirectService.postAuth({
      redirectUri: this.redirectUri(),
      code: account.loginCode,
      ...this.paramsDaContinuacao(),
    });
  }

  /**
   * Continuação do fluxo OIDC, recuperada do `state`.
   *
   * A URL de retorno do provedor externo é fixa por cidade, então o `redirectTo` e o
   * `returnTo` que retomam o /authorize só chegam aqui por dentro do `state` — ver
   * `buildEncodedState`. Sem eles, o `postAuth` cai no app padrão da cidade e o
   * aplicativo que pediu o login nunca recebe o code.
   */
  private continuacaoOidc: Pick<EstadoExterno, 'redirectTo' | 'returnTo'> = {};

  /**
   * O `postAuth` valida o `redirectTo` contra a lista de domínios permitidos, então
   * um `state` adulterado não vira redirecionamento aberto.
   */
  private paramsDaContinuacao() {
    return postAuthContinuation(this.continuacaoOidc);
  }

  callbackSubscribe(params: Record<string, any>) {
    const { strategy, customUrlRedirect, redirectTo, returnTo, intent } =
      decodeState(params['state'] as string);

    this.continuacaoOidc = { redirectTo, returnTo };
    this.customUrlRedirect = customUrlRedirect ?? '';

    if (intent === ACCOUNT_CREATION_INTENT) {
      this.openAccountOptions(params['code']);
      return;
    }

    const url = new URL(location.href);
    const searchParams = new URLSearchParams(url.search);

    if (searchParams.get('redirectTo')) {
      localStorage.setItem(
        'redirectTo',
        searchParams.get('redirectTo') as string
      );
    }

    this._loginByIntegrationService
      .handle({
        cityId: this.cityId,
        connection: this.cityConnection,
        code: params['code'],
        strategy: strategy || 'INTEGRATION',
        ...(customUrlRedirect ? { customUrlRedirect } : {}),
        ...params,
      })
      .then(async (response: LoginByIntegrationOutput) => {
        this.redirectUri.set(response.redirectUri ?? '');

        if (response.requireAccountCreation) {
          await this.createNewAccount();
          return;
        }

        if (response.requireSelectUser) {
          this.canCreateAccount.set(
            strategy === 'GOV_BR' && (await this.acceptsCompanyAccounts())
          );
          this._appHandler.setLoading(false);
          this.requireSelectAccount.set(true);
          this.accounts.set(lastAccessedFirst(response.accounts || []));
          return;
        }

        if (response.requireNewUser) {
          this._router.navigate([`/${this.cityIdentity}/create-account`], {
            queryParams: {
              skipAccountConfirmation: !response.options?.emailVerification,
            },
            state: {
              initialAccountData: response.userData,
              blockedFields: response.options?.blockFields,
            },
          });
          return;
        }

        if (response.registrationToken) {
          this.openRegistrationCompletion(
            response.registrationToken,
            response.userData!
          );
          return;
        }

        if (response.requireCompleteRegistration) {
          this._router.navigate(
            [`/${this.cityIdentity}/complete-registration`],
            {
              state: {
                userData: response.userData,
                token: response.tempToken,
                redirectUri: `${response.redirectUri}?code=${response.code}`,
              },
            }
          );
          return;
        }

        this._redirectService.postAuth({
          redirectUri: response.redirectUri!,
          code: response.code,
          ...this.paramsDaContinuacao(),
        });
      })
      .catch((errorResponse: any) => {
        this._appHandler.clearError();
        this._appHandler.setLoading(false);

        if (errorResponse.error.code === 'emailNotVerified') {
          this._appHandler.setError(true, {
            message: 'A verificação da conta está pendente.',
            description:
              'Ação necessária para acessar o sistema. Clique no botão abaixo.',
            actionText: 'Reenviar e-mail de confirmação de conta',
            action: () => {
              this.resendAccountConfirmation(
                errorResponse.error.params?.userId
              );
            },
          });

          return;
        }

        if (errorResponse.error.code === 'userBlocked') {
          this._appHandler.setError(true, {
            message: 'Sua conta está bloqueada.',
            description:
              'Contate-nos através do chat, localizado no ícone no canto inferior direito, para prosseguir.',
          });

          return;
        }

        if (errorResponse.error.code === 'socialUserNotFound') {
          this._appHandler.setError(true, {
            message: 'Nenhum cadastro localizado',
            description:
              'Não foi encontrado um cadastro no nosso sistema com o email que você selecionou. Deseja realizar o seu cadastro?',
            actionText: 'Criar nova conta',
            action: () => {
              this._appHandler.clearError();
              void this._router.navigate(
                [`/${this.cityIdentity}/create-account`],
                {
                  queryParams: {
                    showWelcomeMessage: false,
                    skipAccountConfirmation:
                      errorResponse.error.params.emailVerified ?? false,
                    email: errorResponse.error.params.email,
                    name: errorResponse.error.params.name,
                  },
                  state: {
                    blockedFields: ['email', 'nome', 'sobrenome'],
                    verification: {
                      code: params['code'],
                      strategy: params['state'],
                      provider: params['provider'],
                    },
                  },
                }
              );
            },
          });

          return;
        }

        const accountConflict = accountConflictError(errorResponse.error);
        if (accountConflict) {
          this._appHandler.setError(true, {
            ...accountConflict,
            actionText: 'Voltar para o login',
            action: () => {
              this._appHandler.clearError();
              this.redirectToLogin();
            },
          });
          return;
        }

        if (errorResponse.error.code === 'certificateExpired') {
          this._appHandler.setError(true, {
            message: 'Certificado expirado.',
            description: 'Não é possível realizar login com um certificado expirado.',
          });
          return;
        }

        this._appHandler.setError(true, {
          message: 'A operação não pôde ser concluída.',
          description:
            'Se o problema persistir, entre em contato com nossa equipe de suporte.',
          actionText: 'Tentar novamente',
          action: () => {
            this._appHandler.clearError();
            this.redirectToLogin();
          },
        });
      });
  }

  async createNewAccount(): Promise<void> {
    const config = await this._authRepository.getConfigByCityId(this.cityIndex);
    this._redirectService.toProvider(
      govBrAccountCreationUrl(
        govBrAuthorizeBaseUrl(config)!,
        this.customUrlRedirect,
        this.continuacaoOidc
      )
    );
  }

  private async acceptsCompanyAccounts(): Promise<boolean> {
    const config = await this._authRepository.getConfigByCityId(this.cityIndex);
    return Boolean(config?.allowUseCnpj && govBrAuthorizeBaseUrl(config));
  }

  private openAccountOptions(code: string): void {
    this._authRepository
      .startAccountCreation({ cityId: this.cityIndex, code })
      .then((accountOptions) =>
        this._router.navigate([`/${this.cityIdentity}/account-options`], {
          state: {
            accountOptions,
            customUrlRedirect: this.customUrlRedirect,
            continuation: this.continuacaoOidc,
          },
        })
      )
      .catch(() => {
        this._appHandler.setLoading(false);
        this._appHandler.setError(true, {
          message: 'Não foi possível continuar o cadastro.',
          description: 'Entre com o gov.br novamente. Se o problema persistir, fale com o suporte.',
          actionText: 'Voltar',
          action: () => {
            this._appHandler.clearError();
            this.redirectToLogin();
          },
        });
      });
  }

  private openRegistrationCompletion(
    registrationToken: string,
    account: User
  ): void {
    const state: RegistrationCompletionState = {
      registrationToken,
      account,
      continuation: this.continuacaoOidc,
    };
    this._router.navigate([`/${this.cityIdentity}/registration-completion`], {
      state,
    });
  }

  finishLogin(): void {
    window.location.href = this.redirectUri();
  }

  private resendAccountConfirmation(userId: string): void {
    this._appHandler.setError(true, {
      message: 'Enviando e-mail de confirmação de conta...',
      description:
        'Este processo pode levar alguns segundos. Por favor, aguarde.',
    });

    this._resendAccountConfirmationService
      .handle({
        cityId: this.cityIndex,
        userId,
      })
      .then(() => {
        this._appHandler.setError(true, {
          message: 'E-mail enviado com sucesso!',
          description:
            'Verifique sua caixa de entrada e siga as instruções para confirmar sua conta.',
        });
      })
      .catch((errorResponse: any) => {
        if (errorResponse.error.code === 'recentVerificationRequest') {
          this._appHandler.setError(true, {
            message: 'Já enviamos o e-mail de confirmação',
            description:
              'Verifique sua caixa de entrada e a pasta de spam e siga as instruções para confirmar sua conta. Se não encontrar o e-mail, aguarde algumas horas para pedir um novo envio.',
          });
          return;
        }

        this._appHandler.setError(true, {
          message: 'Erro ao enviar o e-mail de confirmação de conta!',
          description:
            'Tente novamente ou entre em contato com nossa equipe de suporte.',
          actionText: 'Tentar novamente',
          action: () => {
            this.resendAccountConfirmation(userId);
          },
        });
      });
  }
}
